import __contextModule_0 from "../../../../localize/countries/countries.json"; import __contextModule_1 from "../../../../localize/countries/de.json"; import __contextModule_2 from "../../../../localize/countries/en.json"; import __contextModule_3 from "../../../../localize/countries/es.json"; import __contextModule_4 from "../../../../localize/countries/fr.json"; import __contextModule_5 from "../../../../localize/countries/it.json"; function __requireContext(modules) { const context = (key) => { if (!(key in modules)) throw new Error(`Cannot find module '${key}'`); return modules[key]; }; context.keys = () => Object.keys(modules); return context; } import { isSupportedLanguage } from "@archipad-js/core/translate";

import { IllegalArgumentError, IllegalStateError, UnsupportedOperationError } from "@core/errors/errors-core";

import * as LocalStorage from '@core/drivers/localStorage';
import _LegacyServiceManager from "@core/services/legacy-service-manager";

import env from "@env";

const localeEnv: Record<string, unknown> = { ...env["@core/services/locale"] };

if (!Array.isArray(localeEnv.supportedLocales) || !localeEnv.supportedLocales.length) {
    throw new IllegalStateError("missing config.supportedLocales in env");
}

export const supportedLocales: readonly Intl.Locale[] = localeEnv.supportedLocales.map((locale) => new Intl.Locale(locale));

const localStorage = LocalStorage.driver;

declare const window: Window;

/*----------------------------------------------------------------------------*/

declare global { 
    interface Navigator { 
        location: Location;
        language: string;
        languages?: string[]; // Experimental : @see https://developer.mozilla.org/fr/docs/Web/API/NavigatorLanguage/languages
    }
    interface Location { 
        search: string;  
    }
    interface Window { 
        readonly navigator:Navigator; 
        location: Location;
    }
}

/**
 * Get the default locale
 *
 * @returns {string} default locale.
 */
export function getDefaultLocale(): Intl.Locale {
    let i: number;
    let loc = null;

    if (typeof window === "object") {
        // parse query string
        let query = window.location.search;
        if (query) {
            query = query.substring(1);
            const args = query.split("&");
            for (i = 0; i < args.length; i++) {
                if (!args[i].indexOf("lang=")) {
                    return _validateLocale(args[i].substring(5));
                }
            }
        }

        loc = localStorage.getItem("locale");
        if (loc) {
            return _validateLocale(loc);
        }

        const nav = window.navigator;
        let preferredLanguages = nav.languages; // modern browsers
        if (!preferredLanguages) {
            if (nav.language) {
                // Chrome, Firefox, Safari ....
                preferredLanguages = [nav.language];
            }
        }

        const preferredLocales = preferredLanguages.map((lang) => new Intl.Locale(lang));

        for (const locale of preferredLocales) {
            if (isSupportedLanguage(locale, supportedLocales)) {
                return locale;
            }
        }
    }

    return supportedLocales[0];
}

function _validateLocale(tag: string): Intl.Locale {
    try {
        const locale = new Intl.Locale(tag);
        if (isSupportedLanguage(locale, supportedLocales)) {
            return locale;
        }
        return supportedLocales[0];
    } catch (e) {
        return supportedLocales[0];
    }
}

/*---------------------------------------------------------------------------*/

export async function loadLang(locale): Promise<{code: string; label: string}[]> {
    // use require instead of import here to avoid issues when compiling to CommonJS
    const list = __requireContext({ "@localize/countries/countries.json": __contextModule_0, "@localize/countries/de.json": __contextModule_1, "@localize/countries/en.json": __contextModule_2, "@localize/countries/es.json": __contextModule_3, "@localize/countries/fr.json": __contextModule_4, "@localize/countries/it.json": __contextModule_5 })(`@localize/countries/${locale}.json`)
    
    return list.countries.sort(function (a, b) {
        const countryA = a.label.toUpperCase();
        const countryB = b.label.toUpperCase();
        return (countryA < countryB) ? -1 : (countryA > countryB) ? 1 : 0;
    });
}

//#region Moment

export function convertFromMoment(format:string): string {
    return MomentHelper.convertFromMoment(format);
}

export class MomentHelper {

    public static readonly date_short = 'L';

    /**
     * Convert strange moment date format to common
     * @param format 
     */
    public static convertFromMoment(format:string): string {
        // sanity check : assert NOT date-fns localizable format
        if (/[Pp]+/.test(format)) {
            throw new IllegalArgumentError(`expected Moment format, found Date-FNS localizable format | format:${format}`);
        }
        // convert localizable formats
        switch (format) {
            case this.date_short:
                return 'P';
        }
        // convert localized formats
        return this.convertLocalizedFormatFromMoment(format);
    }

    private static readonly ConvertTable = {
        "D" : "d", // Day in month
        "d" : 'e', // Day name in week
        "M" : "M", // Month in year
        "Y" : "y", // Year
        "H" : "H", // Hour in day (0-23)
        "h" : "h", // Hour in am/pm (1-12)
        "A" : "a", // Am/pm marker
        "a" : "a", // Am/pm marker
        "m" : "m", // Minute in hour
        "s" : "s", // Second in minute
        "[": "'", // escape char
        "]": "'", // escape char
    };

    /**
     * Convert strange moment date format to common
     * Localizable format MUST have been already resolved.
     * @see https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
     * @param format 
     */
    private static convertLocalizedFormatFromMoment(format:string): string {
        const formatArray = format.split('');
        for(let i=0; i<formatArray.length; i++) {
            const c = formatArray[i];
            let cc: string;
            if (!/([a-zA-Z]|(\[|\]))/.test(c)) {
                cc = c;
            }
            else {
                cc = this.ConvertTable[c];
                if (cc === null) {
                    throw new UnsupportedOperationError(`NotSupported | format:${format} character:${c}`);
                }
                else if (!cc) {
                    throw new IllegalArgumentError(`IllegalArgument | format:${format} character:${c}`);
                }   
            }
            formatArray[i] = cc;
        }

        return formatArray.join('');
    }
}

//#endregion
